import React, { useState, useEffect } from "react"
import {
  Row, Col, Card, CardHeader, CardBody, Spinner, Alert,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { useParams, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import {
  getCompanySubscriptionHistory,
} from "../../api/requests/subscription-requests"
import { fetchAdminCompany } from "../../api/requests/company-requests"
import { downloadSubscriptionInvoicePdf } from "../../utils/subscriptionInvoicePdf"
import { AssignSubscriptionModal } from "../modals/subscription/AssignSubscriptionModal"
import RefundModal from "../modals/subscription/RefundModal"
import EditSubscriptionSettingsModal from "../modals/subscription/EditSubscriptionSettingsModal"
import "../../assets/css/page-common.css"
import BackButton from "../../components/Common/BackButton";
import { formatLicenseDate, isUnlimitedDate } from "../../utils/subscriptionDates"

const getCompanyDetail = async (companyId) => {
  const res = await fetchAdminCompany(companyId)
  if (!res.ok) {
    const status = res.status
    if (status === 401 || status === 403) throw { authError: true }
    throw new Error((await res.json().catch(() => ({}))).message || "Firma bilgisi alınamadı")
  }
  return res.json()
}

const NAVY = "#221C5D"
const ORANGE = "#E56A22"
const GREEN = "#71C30B"
const RED = "#dc3545"

const formatPrice = (price) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price || 0) + " ₺"

const formatDate = (d) => formatLicenseDate(d)

const STATUS_BADGE = {
  ACTIVE:    { label: "Aktif",          color: GREEN,   bg: "rgba(113,195,11,.1)" },
  TRIAL:     { label: "Deneme",         color: ORANGE,  bg: "rgba(229,106,34,.1)" },
  PENDING:   { label: "Ödeme Bekliyor", color: ORANGE,  bg: "rgba(229,106,34,.1)" },
  EXPIRED:   { label: "Süresi Dolmuş", color: "#6c757d", bg: "rgba(108,117,125,.1)" },
  CANCELLED: { label: "İptal",          color: "#E07A7A", bg: "rgba(224,122,122,.1)" },
  UPGRADED:  { label: "Yükseltilmiş",  color: NAVY,    bg: "rgba(34,28,93,.1)" },
  SCHEDULED: { label: "Planlandı",      color: NAVY,    bg: "rgba(34,28,93,.1)" },
}

const StatusBadge = ({ status, large }) => {
  const cfg = STATUS_BADGE[status] || { label: status, color: "#B0B7C3", bg: "rgba(176,183,195,.1)" }
  return (
    <span
      className="badge border-0"
      style={{
        borderRadius: 999,
        padding: large ? ".45rem .85rem" : ".35rem .6rem",
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: large ? ".85rem" : ".78rem",
        border: `1px solid ${cfg.color}20`,
      }}
    >
      {cfg.label}
    </span>
  )
}

const SubscriptionDetailPage = () => {
  const { companyId } = useParams()
  const navigate = useNavigate()

  const [company, setCompany] = useState(null)
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Modals
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [selectedRefundSub, setSelectedRefundSub] = useState(null)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [selectedSettingsSub, setSelectedSettingsSub] = useState(null)

  // History pagination
  const [historyPage, setHistoryPage] = useState(0)
  const [historyPageSize, setHistoryPageSize] = useState(10)

  const fetchData = async () => {
    try {
      setError(null)
      setLoading(true)
      const [companyRes, subsRes] = await Promise.all([
        getCompanyDetail(companyId),
        getCompanySubscriptionHistory(companyId),
      ])
      if (companyRes?.statusCode === 200) setCompany(companyRes.data)
      setSubscriptions(subsRes.data || [])
    } catch (err) {
      if (err.authError) { navigate("/login"); return }
      setError(err.message || "Veriler yüklenirken hata oluştu")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    document.title = "Abonelik Detay | Tahsilat360"
    if (companyId) fetchData()
  }, [companyId]) // eslint-disable-line

  const activeSub = subscriptions.find(s => s.status === "ACTIVE" || s.status === "TRIAL")

  const handleDownloadInvoice = async (sub) => {
    try {
      await downloadSubscriptionInvoicePdf({ ...sub, companyName: company?.companyName })
    } catch {
      toast.error("Fatura indirilemedi")
    }
  }

  const onAssignSuccess = () => { setAssignModalOpen(false); fetchData(); toast.success("Plan değişikliği başarılı") }
  const onRefundSuccess = () => { setRefundModalOpen(false); setSelectedRefundSub(null); fetchData() }
  const onSettingsSuccess = () => { setSettingsModalOpen(false); setSelectedSettingsSub(null); fetchData() }

  if (loading) {
    return (
      <div className="page-content text-center p-5">
        <Spinner size="lg" color="primary" />
        <h6 className="mt-3 text-muted">Yükleniyor...</h6>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <Alert color="danger" className="rounded-4">
          <i className="bx bx-error-circle me-2" />
          {error}
          <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={fetchData}>
            Tekrar Dene
          </button>
        </Alert>
      </div>
    )
  }

  const successfulSubs = subscriptions.filter(s => s.status !== "CANCELLED" && s.status !== "PENDING" && !s.refunded)
  const totalPaid = successfulSubs.reduce((sum, s) => sum + (s.paidAmount || 0), 0)
  const totalRefunded = subscriptions.filter(s => s.refunded).reduce((sum, s) => sum + (s.paidAmount || 0), 0)

  const historyTotalPages = Math.ceil(subscriptions.length / historyPageSize)
  const paginatedSubs = subscriptions.slice(historyPage * historyPageSize, (historyPage + 1) * historyPageSize)

  const getHistoryPages = () => {
    const arr = []
    const max = 5
    let start = Math.max(0, historyPage - Math.floor(max / 2))
    let end = Math.min(historyTotalPages - 1, start + max - 1)
    if (end - start + 1 < max) start = Math.max(0, end - max + 1)
    for (let i = start; i <= end; i++) arr.push(i)
    return arr
  }

  const daysColor = (d) => d <= 7 ? RED : d <= 30 ? ORANGE : GREEN

  const usagePercent = (current, max) => max ? Math.min(100, (current / max) * 100) : 0
  const usageColor = (current, max) => {
    if (!max) return GREEN
    const pct = (current / max) * 100
    return pct > 90 ? RED : pct > 70 ? ORANGE : GREEN
  }

  return (
    <div className="page-content page-common">
      <div className="container-fluid">

        {/* ── Page Header ── */}
        <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <BackButton />
            <div>
              <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                {company?.companyName || "Firma"}
              </h5>
              <span className="text-muted" style={{ fontSize: ".82rem" }}>
                Abonelik Detay Sayfası
              </span>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
              style={{ height: 34, fontSize: ".8rem" }}
              onClick={() => setAssignModalOpen(true)}
            >
              <i className="bx bx-transfer" style={{ fontSize: 16 }} /> Plan Değiştir
            </button>
            {activeSub && (
              <button
                className="btn btn-rounded d-flex align-items-center gap-1 px-3"
                style={{ height: 34, fontSize: ".8rem", background: "#475569", borderColor: "#475569", color: "#fff" }}
                onClick={() => { setSelectedSettingsSub(activeSub); setSettingsModalOpen(true) }}
              >
                <i className="bx bx-cog" style={{ fontSize: 16 }} /> Ayarlar
              </button>
            )}
          </div>
        </div>

        {/* ── Firma Bilgileri ── */}
        <Card className="detail-card mb-3">
          <CardHeader>
            <div className="section-title">
              <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                <i className="bx bx-buildings" />
              </div>
              Firma Bilgileri
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              {[
                { label: "Firma Adı", value: company?.companyName },
                { label: "Firma Kodu", value: company?.companyCode },
                { label: "Yetkili", value: company?.contactFullName },
                { label: "E-posta", value: company?.contactEmail, small: true },
                { label: "Telefon", value: company?.phoneNumber },
              ].map((f, i) => (
                <Col key={i} xs={6} md className="info-field">
                  <div className="info-label">{f.label}</div>
                  <div className="info-value" style={f.small ? { fontSize: ".85rem" } : {}}>
                    {f.value || "-"}
                  </div>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>

        {/* ── İstatistik Kutucukları ── */}
        <div className="stat-chip-row mb-3">
          {[
            { label: "Toplam İşlem", value: subscriptions.length, color: NAVY, bg: "rgba(34,28,93,.1)", icon: "bx-receipt" },
            { label: "Toplam Ödeme", value: formatPrice(totalPaid), color: GREEN, bg: "rgba(113,195,11,.1)", icon: "bx-wallet" },
            { label: "İade Edilen", value: formatPrice(totalRefunded), color: RED, bg: "rgba(220,53,69,.1)", icon: "bx-undo" },
            { label: "İptal Sayısı", value: subscriptions.filter(s => s.status === "CANCELLED").length, color: ORANGE, bg: "rgba(229,106,34,.1)", icon: "bx-block" },
          ].map((s, i) => (
            <div key={i} className="stat-chip">
              <div className="stat-icon" style={{ background: s.bg, color: s.color }}>
                <i className={`bx ${s.icon}`} />
              </div>
              <div className="stat-number" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Mevcut Abonelik + Haklar ── */}
        <Row className="mb-3 g-3">
          <Col lg={activeSub ? 8 : 12}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(113,195,11,.1)", color: GREEN }}>
                    <i className="bx bx-package" />
                  </div>
                  Mevcut Abonelik
                </div>
              </CardHeader>
              <CardBody>
                {activeSub ? (
                  <>
                    <Row className="mb-3">
                      <Col sm={4} className="info-field">
                        <div className="info-label">Plan</div>
                        <div className="info-value" style={{ color: NAVY, fontSize: "1.15rem" }}>
                          {activeSub.planName}
                        </div>
                      </Col>
                      <Col sm={3} className="info-field">
                        <div className="info-label">Durum</div>
                        <div className="mt-1"><StatusBadge status={activeSub.status} large /></div>
                      </Col>
                      <Col sm={2} className="info-field">
                        <div className="info-label">Kalan Gün</div>
                        <div className="info-value" style={{
                          color: daysColor(activeSub.daysRemaining ?? 0),
                          fontSize: "1.3rem",
                        }}>
                          {isUnlimitedDate(activeSub.endDate) ? "Süresiz" : (activeSub.daysRemaining ?? "-")}
                        </div>
                      </Col>
                      <Col sm={3} className="info-field">
                        <div className="info-label">Ödenen Tutar</div>
                        <div className="info-value">{formatPrice(activeSub.paidAmount)}</div>
                      </Col>
                    </Row>
                    <div style={{ borderTop: "1px solid #f0f2f5", paddingTop: ".75rem" }}>
                      <Row>
                        <Col sm={3} className="info-field">
                          <div className="info-label">Başlangıç</div>
                          <div className="info-value">{formatDate(activeSub.startDate)}</div>
                        </Col>
                        <Col sm={3} className="info-field">
                          <div className="info-label">Bitiş</div>
                          <div className="info-value">{formatDate(activeSub.endDate)}</div>
                        </Col>
                        <Col sm={3} className="info-field">
                          <div className="info-label">Özel Domain</div>
                          <div className="info-value">
                            {activeSub.allowCustomDomain
                              ? <span style={{ color: GREEN }}><i className="bx bx-check-circle" /> Açık</span>
                              : <span className="text-muted"><i className="bx bx-x-circle" /> Kapalı</span>}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="bx bx-package" style={{ fontSize: 48, opacity: .3 }} />
                    <div className="mt-2">Aktif abonelik bulunamadı</div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {activeSub && (
            <Col lg={4}>
              <Card className="detail-card h-100">
                <CardHeader>
                  <div className="section-title">
                    <div className="section-icon" style={{ background: "rgba(229,106,34,.1)", color: ORANGE }}>
                      <i className="bx bx-shield-quarter" />
                    </div>
                    Haklar & Limitler
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="rights-grid">
                    {/* Cari Hesap */}
                    <div className="right-item">
                      <div className="right-header">
                        <span className="right-label"><i className="bx bx-user me-1" />Cari Hesap</span>
                        <span className="right-value">
                          {activeSub.currentAccounts} <span style={{ color: "#94a3b8", fontWeight: 400 }}>/ {activeSub.maxAccounts || "∞"}</span>
                        </span>
                      </div>
                      {activeSub.maxAccounts && (
                        <div className="progress-bar-track">
                          <div
                            className="progress-bar-fill"
                            style={{
                              width: `${usagePercent(activeSub.currentAccounts, activeSub.maxAccounts)}%`,
                              backgroundColor: usageColor(activeSub.currentAccounts, activeSub.maxAccounts),
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Alt kullanıcı (firma personeli) ve cari kullanıcısı AYRI havuzlar;
                        biri dolup diğeri boş olabildiği için tek çubukta gösterilemezler. */}
                    <div className="right-item">
                      <div className="right-header">
                        <span className="right-label"><i className="bx bx-group me-1" />Alt Kullanıcı</span>
                        <span className="right-value">
                          {activeSub.currentUsers} <span style={{ color: "#94a3b8", fontWeight: 400 }}>/ {activeSub.maxUsers || "∞"}</span>
                        </span>
                      </div>
                      {activeSub.maxUsers && (
                        <div className="progress-bar-track">
                          <div
                            className="progress-bar-fill"
                            style={{
                              width: `${usagePercent(activeSub.currentUsers, activeSub.maxUsers)}%`,
                              backgroundColor: usageColor(activeSub.currentUsers, activeSub.maxUsers),
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="right-item">
                      <div className="right-header">
                        <span className="right-label"><i className="bx bx-user-circle me-1" />Cari Kullanıcı</span>
                        <span className="right-value">
                          {activeSub.currentAccountUsers ?? 0} <span style={{ color: "#94a3b8", fontWeight: 400 }}>/ {activeSub.maxAccountUsers || "∞"}</span>
                        </span>
                      </div>
                      {activeSub.maxAccountUsers && (
                        <div className="progress-bar-track">
                          <div
                            className="progress-bar-fill"
                            style={{
                              width: `${usagePercent(activeSub.currentAccountUsers, activeSub.maxAccountUsers)}%`,
                              backgroundColor: usageColor(activeSub.currentAccountUsers, activeSub.maxAccountUsers),
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        {/* ── Abonelik Geçmişi Tablosu ── */}
        <Card className="detail-card">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <div className="section-title">
                <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                  <i className="bx bx-history" />
                </div>
                Abonelik Geçmişi
              </div>
              <span className="text-muted" style={{ fontSize: ".82rem" }}>
                {subscriptions.length} kayıt
              </span>
            </div>
          </CardHeader>
          <CardBody className="pt-0">
            {subscriptions.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="bx bx-package" style={{ fontSize: 48, opacity: .3 }} />
                <div className="mt-2">Abonelik kaydı bulunamadı</div>
              </div>
            ) : (
              <>
              <div className="table-responsive rounded-3" style={{ border: "1px solid #e2e8f0" }}>
                <Table className="table table-hover detail-table mb-0">
                  <Thead>
                    <Tr>
                      <Th>Plan</Th>
                      <Th>Durum</Th>
                      <Th className="d-none d-md-table-cell">Başlangıç</Th>
                      <Th className="d-none d-md-table-cell">Bitiş</Th>
                      <Th className="d-none d-lg-table-cell">Kalan Gün</Th>
                      <Th>Tutar</Th>
                      <Th className="text-end">İşlemler</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedSubs.map((sub, idx) => {
                      const canRefund = sub.paidAmount > 0 && !sub.refunded
                      const isActive = sub.status === "ACTIVE" || sub.status === "TRIAL"
                      return (
                        <Tr key={sub.id || idx}>
                          <Td>
                            <span className="fw-semibold" style={{ color: "#1e293b" }}>{sub.planName}</span>
                          </Td>
                          <Td><StatusBadge status={sub.status} /></Td>
                          <Td className="d-none d-md-table-cell text-muted">{formatDate(sub.startDate)}</Td>
                          <Td className="d-none d-md-table-cell text-muted">{formatDate(sub.endDate)}</Td>
                          <Td className="d-none d-lg-table-cell">
                            {isActive ? (
                              <span style={{ fontWeight: 700, color: daysColor(sub.daysRemaining ?? 0) }}>
                                {isUnlimitedDate(sub.endDate) ? "Süresiz" : (sub.daysRemaining ?? "-")}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </Td>
                          <Td>
                            <span className="fw-semibold">{formatPrice(sub.paidAmount)}</span>
                            {sub.refunded && (
                              <span
                                className="badge ms-1"
                                style={{
                                  borderRadius: 999, fontSize: ".6rem",
                                  background: "rgba(220,53,69,.1)", color: RED,
                                  border: `1px solid ${RED}30`, fontWeight: 600,
                                }}
                              >
                                İade
                              </span>
                            )}
                          </Td>
                          <Td className="text-end">
                            <div className="d-flex gap-1 justify-content-end">
                              <button
                                className="btn btn-sm action-btn"
                                style={{ background: "rgba(34,28,93,.1)", color: NAVY, border: "none" }}
                                title="Abonelik Ayarları"
                                onClick={() => { setSelectedSettingsSub(sub); setSettingsModalOpen(true) }}
                              >
                                <i className="bx bx-cog" />
                              </button>
                              <button
                                className="btn btn-sm action-btn"
                                style={{ background: "rgba(108,117,125,.1)", color: "#475569", border: "none" }}
                                title="Fatura İndir"
                                onClick={() => handleDownloadInvoice(sub)}
                              >
                                <i className="bx bx-download" />
                              </button>
                              <button
                                className="btn btn-sm action-btn"
                                style={{
                                  background: canRefund ? "rgba(229,106,34,.1)" : "rgba(0,0,0,.03)",
                                  color: canRefund ? ORANGE : "#c0c0c0",
                                  border: "none",
                                  cursor: canRefund ? "pointer" : "not-allowed",
                                }}
                                title={canRefund ? "İade İşaretle" : sub.refunded ? "İade edilmiş" : "Ödeme tutarı yok"}
                                onClick={() => { if (canRefund) { setSelectedRefundSub(sub); setRefundModalOpen(true) } }}
                                disabled={!canRefund}
                              >
                                <i className="bx bx-money" />
                              </button>
                            </div>
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </div>

              {/* Pagination */}
              {subscriptions.length > historyPageSize && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="text-muted" style={{ fontSize: ".85rem" }}>
                    {subscriptions.length} kayıttan{" "}
                    {historyPage * historyPageSize + 1} - {Math.min((historyPage + 1) * historyPageSize, subscriptions.length)}{" "}
                    arası gösteriliyor
                  </div>
                  <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
                    <div className="d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
                      <span className="text-muted" style={{ fontSize: ".85rem" }}>Sayfa başına</span>
                      <select
                        className="form-select form-select-sm rounded-pill"
                        style={{ minWidth: 70, paddingRight: "1.75rem" }}
                        value={historyPageSize}
                        onChange={(e) => { setHistoryPageSize(parseInt(e.target.value)); setHistoryPage(0) }}
                      >
                        {[10, 25, 50].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <nav>
                      <ul className="pagination pagination-sm mb-0" style={{ gap: 2 }}>
                        <li className={`page-item ${historyPage === 0 ? "disabled" : ""}`}>
                          <button className="page-link rounded-pill" onClick={() => setHistoryPage(historyPage - 1)} disabled={historyPage === 0}>
                            <i className="bx bx-chevron-left" />
                          </button>
                        </li>
                        {getHistoryPages().map(p => (
                          <li key={p} className={`page-item ${historyPage === p ? "active" : ""}`}>
                            <button
                              className="page-link rounded-pill"
                              onClick={() => setHistoryPage(p)}
                              style={historyPage === p ? { backgroundColor: NAVY, borderColor: NAVY } : {}}
                            >
                              {p + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${historyPage >= historyTotalPages - 1 ? "disabled" : ""}`}>
                          <button className="page-link rounded-pill" onClick={() => setHistoryPage(historyPage + 1)} disabled={historyPage >= historyTotalPages - 1}>
                            <i className="bx bx-chevron-right" />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Modals */}
      <AssignSubscriptionModal
        isOpen={assignModalOpen}
        toggle={() => setAssignModalOpen(false)}
        company={company ? { id: company.id, companyName: company.companyName } : null}
        onSuccess={onAssignSuccess}
      />
      <RefundModal
        isOpen={refundModalOpen}
        toggle={() => { setRefundModalOpen(false); setSelectedRefundSub(null) }}
        subscription={selectedRefundSub}
        onConfirm={onRefundSuccess}
      />
      <EditSubscriptionSettingsModal
        isOpen={settingsModalOpen}
        toggle={() => { setSettingsModalOpen(false); setSelectedSettingsSub(null) }}
        subscription={selectedSettingsSub}
        onSuccess={onSettingsSuccess}
      />
    </div>
  )
}

export default SubscriptionDetailPage
